'use client';

import { useMemo } from 'react';
import { writeClipboard } from '@/hooks/dom/use-clipboard';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';
import { elementToBlob, IMAGE_BLOB_TYPE, type ElementToBlobVars } from './use-element-to-blob';

export const IMAGE_COPY_TYPE = {
  Png: 'png',
  Jpeg: 'jpeg',
} as const;
export type ImageCopyType = typeof IMAGE_COPY_TYPE[keyof typeof IMAGE_COPY_TYPE];

export interface CopyElementAsImageVars {
  element: HTMLElement;
  type: ImageCopyType;
  options?: ElementToBlobVars['options'];
}

async function copyElementAsImage(vars: CopyElementAsImageVars): Promise<void> {
  if (typeof window === 'undefined' || typeof window.ClipboardItem === 'undefined') {
    throw new Error('ClipboardItem is not supported.');
  }
  const blob = await elementToBlob({
    element: vars.element,
    type: vars.type === IMAGE_COPY_TYPE.Jpeg ? IMAGE_BLOB_TYPE.Jpeg : IMAGE_BLOB_TYPE.Png,
    options: vars.options,
  });
  await writeClipboard({ items: [new window.ClipboardItem({ [blob.type]: blob })] });
}

export function useCopyElementAsImage(): ActionResult<CopyElementAsImageVars, void> {
  return useActionHook<CopyElementAsImageVars, void>(
    useMemo(
      () => ({ mutationFn: copyElementAsImage }),
      [],
    ),
  );
}
